import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useIntl } from "react-intl";
import { useAuth } from "../../auth/AuthContext";
import { hasAnyRole } from "../../auth/authUi";
import { moveWorkOrderPlannedDates } from "../../fetchers/cmmsWorkOrder";
import { pushErrorMessage } from "../../utils/notifications";
import { CMMS_MANAGER_ROLES } from "../cmms/cmmsRoles";
import { formatCmmsError } from "../cmms/components/CmmsStates";
import { getMovedStart, getUndoStart } from "./workOrderWeek";
import messages from "./messages";

/*
 * Moving a card to another day in the week or month overview, and taking the move back.
 *
 * Only the planned dates move, through an endpoint of their own (moveWorkOrderPlannedDates).
 * Managers only, the server's own rule (Cmms_Manager), so canMove says whether a view offers a
 * move at all, and it never offers one that would be refused. Where a row may go is
 * workOrderWeek.js's to say.
 *
 * containerRef is the element the view's cards are in. A moved card is drawn anew on its new day,
 * and the one that had focus is gone, so focus is sent after it rather than left to fall back to
 * the page. It is looked for each time layout, what the view draws its cards from, changes.
 * periodKey changes as the view steps to another week or month, where the card was never moved
 * to, and focus is not sent there.
 *
 * As { canMove, movingId, handleMove, notice, closeNotice, handleUndo }. handleMove(row, day)
 * moves a row's order to the day. notice, closeNotice and handleUndo are MoveUndoSnackbar's.
 */
export const useWorkOrderMove = ({ companyKey, containerRef, periodKey, layout }) => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { roleIds } = useAuth();
  const canMove = Boolean(companyKey) && hasAnyRole(roleIds ?? [], CMMS_MANAGER_ROLES);
  const [movingId, setMovingId] = useState(null);
  // Kept after it closes, so the snackbar still has its words while it fades out.
  const [notice, setNotice] = useState({ open: false, move: null });
  const [refocus, setRefocus] = useState(null);

  const moveMutation = useMutation({
    mutationFn: ({ id, start }) => moveWorkOrderPlannedDates(id, { companyKey, plannedStartDate: start.toISOString() }),
  });

  const findCard = (id) => containerRef.current?.querySelector(`[data-work-order-id="${id}"]`) ?? null;

  /*
   * Puts the server's new dates on the order wherever the lists hold it, so the card is on its
   * new day at once rather than after a refetch, and refetches them to be sure. Only the dates:
   * the rest of a cached order is the list's own, and /mine has a shape of its own.
   */
  const applyMovedDates = (updated) => {
    const withNewDates = (list) => (Array.isArray(list)
      ? list.map((item) => (item.id === updated.id
        ? { ...item, plannedStartDate: updated.plannedStartDate, plannedEndDate: updated.plannedEndDate }
        : item))
      : list);
    ["cmmsWorkOrders", "cmmsMyWorkOrders"].forEach((name) => {
      queryClient.setQueriesData({ queryKey: [name, companyKey] }, withNewDates);
      queryClient.invalidateQueries({ queryKey: [name, companyKey] });
    });
    queryClient.invalidateQueries({ queryKey: ["cmmsWorkOrder", companyKey, updated.id] });
    setRefocus({ id: updated.id, from: findCard(updated.id) });
  };

  // The card on its new day is a new element. Until it is drawn, the old one is all there is.
  useEffect(() => {
    if (!refocus) return;
    const card = containerRef.current?.querySelector(`[data-work-order-id="${refocus.id}"]`);
    if (card && card !== refocus.from) {
      card.focus();
      setRefocus(null);
    }
  }, [refocus, layout, containerRef]);

  useEffect(() => setRefocus(null), [periodKey]);

  const moveTo = async (workOrder, start) => {
    setMovingId(workOrder.id);
    try {
      applyMovedDates(await moveMutation.mutateAsync({ id: workOrder.id, start }));
      return true;
    } catch (moveError) {
      pushErrorMessage(formatCmmsError(moveError, intl.formatMessage(messages.weekMoveFailed)));
      return false;
    } finally {
      setMovingId(null);
    }
  };

  // The order as it was before the move, so that undoing puts back what was there (getUndoStart).
  // Not while a move is still on its way: the second one would be dropped unsaid.
  const handleMove = async (row, day) => {
    if (movingId !== null) return;
    const { workOrder } = row;
    if (await moveTo(workOrder, getMovedStart(workOrder, day))) {
      setNotice({ open: true, move: { key: Date.now(), workOrder, day, undoStart: getUndoStart(workOrder) } });
    }
  };

  const closeNotice = () => setNotice((current) => ({ ...current, open: false }));

  const handleUndo = () => {
    const { move } = notice;
    closeNotice();
    if (move?.undoStart && movingId === null) moveTo(move.workOrder, move.undoStart);
  };

  return { canMove, movingId, handleMove, notice, closeNotice, handleUndo };
};
